import { startTransition, useCallback, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Image as ImageIcon, Share2, Star } from 'lucide-react';
import { Button } from '../Button';
import { formatCurrency } from '../FormatCurrency';
import { AddFavourite } from '../../features/favourite/AddFavourite';
import { handleShareProduct } from '../../features/product/ShareProduct';
import { optimizeImageUrl } from '../optimizeImageUrl';
import { useProductCard } from './useProductCard';
import { useAddToCartModal } from './AddToCartModalContext';

const TransitionLink = ({ to, children, className, ...props }) => {
  const navigate = useNavigate();

  const handleClick = useCallback(
    (e) => {
      e.preventDefault();
      startTransition(() => {
        navigate(to);
      });
    },
    [navigate, to]
  );

  return (
    <Link to={to} onClick={handleClick} className={className} {...props}>
      {children}
    </Link>
  );
};

const cardStyles = {
  base: 'w-fit rounded-2xl transition-all duration-300 ease-in-out hover:drop-shadow-[0_4px_6px_rgba(0,0,0,0.25)] pb-2',
  transform: { transform: 'scale(1)', transformOrigin: 'center' },
};

const ProductCardSkeleton = () => (
  <article
    className={cardStyles.base}
    tabIndex={0}
    role="article"
    aria-label={`Loading product`}
    style={cardStyles.transform}
  >
    <div className="relative bg-[#F2F2F2] w-[146px] h-[146px] md:w-[218px] md:h-[218px] rounded-2xl">
      <div className="absolute top-2 flex justify-between w-full px-2">
        <div className="rounded-full bg-gray-200 w-8 h-8 animate-pulse" />
        <div className="bg-gray-200 w-[51px] h-6 rounded-[20px] animate-pulse" />
      </div>
      <div className="flex justify-center items-center mx-auto w-[80px] h-[99px] md:w-[136px] md:h-[169px]">
        <div className="w-full h-3/4 bg-gray-200 rounded animate-pulse mt-10" />
      </div>
    </div>
    <div className="grid grid-cols-1 space-y-2 w-[146px] md:w-[218px] mt-2 px-2">
      <div className="h-12 bg-gray-200 rounded animate-pulse" />
      <div className="flex flex-col space-y-2">
        <div className="h-5 w-1/2 bg-gray-200 rounded animate-pulse" />
        <div className="h-4 w-1/3 bg-gray-200 rounded animate-pulse" />
      </div>
      <div className="flex items-center space-x-1">
        <div className="w-3 h-3 bg-gray-200 rounded-full animate-pulse" />
        <div className="h-4 w-1/4 bg-gray-200 rounded animate-pulse" />
      </div>
      <div className="flex items-center space-x-6">
        <div className="h-10 w-16 bg-gray-200 rounded animate-pulse" />
        <div className="h-6 w-6 bg-gray-200 rounded animate-pulse" />
      </div>
    </div>
  </article>
);

// Text renders immediately; only the image area shimmers until it loads.
const ProductImage = ({ src, alt, priority = false }) => {
  const [status, setStatus] = useState(src ? 'loading' : 'error');

  // Cached images can finish before React attaches onLoad.
  const imgRef = useCallback((img) => {
    if (img?.complete && img.naturalWidth > 0) setStatus('loaded');
  }, []);

  if (status === 'error') {
    return <ImageIcon className="w-1/2 h-1/2 text-gray-300" aria-hidden />;
  }

  return (
    <>
      {status === 'loading' && (
        <div
          className="absolute inset-0 rounded-2xl bg-gray-200 animate-pulse"
          aria-hidden
        />
      )}
      <img
        ref={imgRef}
        src={src}
        alt={alt}
        // First-row cards download ahead of everything below the fold
        loading={priority ? 'eager' : 'lazy'}
        fetchpriority={priority ? 'high' : 'low'}
        decoding="async"
        onLoad={() => setStatus('loaded')}
        onError={() => setStatus('error')}
        className={`max-h-full max-w-full object-contain transition-opacity rounded-2xl mix-blend-multiply duration-300 ${
          status === 'loaded' ? 'opacity-100' : 'opacity-0'
        }`}
      />
    </>
  );
};

const ProductCard = ({ product, isLoading, priority = false }) => {
  const { openModal } = useAddToCartModal();

  const {
    user,
    productID,
    inStock,
    productName,
    coverImage,
    fairAppPrice,
    slug,
    fairAppSalesPrice,
    minimumDownPaymentPercentage,
    average_rating,
    total_review_count,
    discountPercent,
    promo,
    category,
    sub_category,
    handleAddToRecentlyViewed,
  } = useProductCard(product);

  if (isLoading) return <ProductCardSkeleton />;

  return (
    <article
      className={cardStyles.base}
      tabIndex={0}
      role="article"
      aria-label={`Product: ${productName}`}
      style={cardStyles.transform}
      onClick={handleAddToRecentlyViewed}
    >
      <TransitionLink
        to={`/${category}/${sub_category}/${productID}/${slug?.replace(/\//g, '').replace(/\//g, '')}`}
        className="relative block"
      >
        {promo && (
          <img
            src={optimizeImageUrl({ url: promo.image, width: 120 })}
            alt={promo.label}
            loading={priority ? 'eager' : 'lazy'}
            decoding="async"
            className="absolute -top-3 -right-1 md:-top-4 z-20 w-8 h-8 md:w-11 md:h-11 rounded-full object-cover pointer-events-none"
          />
        )}
        <div className="relative bg-[#F2F2F2] w-[146px] h-[146px] md:w-[218px] md:h-[218px] rounded-2xl cursor-pointer flex justify-center items-center p-1 overflow-hidden">
          <div className="absolute top-3 z-10 flex justify-between w-full px-3">
            <p className="bg-white/90 rounded-md p-1 font-normal text-[10px] lg:text-xs">
              <span className=" font-bold">
                {Number(minimumDownPaymentPercentage?.split('.')[0]) === 100
                  ? 'Full payment'
                  : `${minimumDownPaymentPercentage?.split('.')[0]}% down payment`}
              </span>
            </p>
            {Number(fairAppSalesPrice) > 0 && !promo && (
              <span className="hidden md:block rounded-full bg-[#FFDE11] text-xs px-2 py-1">
                {Math.ceil(discountPercent)}% off
              </span>
            )}
          </div>
          <div className="flex justify-center items-center w-full h-full">
            <ProductImage
              key={coverImage}
              // Box is at most 159px wide; 320 covers 2x screens
              src={optimizeImageUrl({ url: coverImage, width: 320 })}
              alt={productName}
              priority={priority}
            />
          </div>
        </div>
      </TransitionLink>
      <div
        className={`${inStock === 'true' || inStock === '1' ? '' : 'opacity-50'} grid grid-cols-1 space-y-2 text-[#222224] w-[146px] md:w-[218px] mt-2 px-2`}
      >
        <TransitionLink
          to={`/${category}/${sub_category}/${productID}/${slug?.replace(/\//g, '')}`}
          className="hover:underline focus:underline focus:outline-none"
        >
          <p className="font-duplet text-xs lg:text-sm font-normal min-h-10 pb-0">
            {productName?.length > 50
              ? productName.slice(0, 30) + '...'
              : productName}
          </p>
        </TransitionLink>
        <div className="flex items-center lg:space-x-1">
          <Star fill="black" size={14} aria-hidden="true" />
          <p className="font-duplet text-sm">
            {' '}
            {average_rating || '0'}
            <span className="ml-1">({total_review_count || '0'})</span>{' '}
          </p>
        </div>
        <div className="flex flex-col lg:flex-row lg:space-x-2 md:gap-x-3 lg:items-center space-y-1">
          <p className="font-duplet font-semibold text-base">
            {Number(fairAppSalesPrice) > 0
              ? formatCurrency(Math.ceil(Number(fairAppSalesPrice)))
              : formatCurrency(Math.ceil(Number(fairAppPrice)))}
          </p>
          {Number(fairAppSalesPrice) > 0 && (
            <p className="text-sm line-through text-[#96959F]">
              {formatCurrency(Math.ceil(Number(fairAppPrice)))}
            </p>
          )}
        </div>
        <div className="flex items-center justify-between lg:flex-col lg:items-start lg:space-y-3">
          <div className="lg:w-[90%] flex justify-between items-center space-x-1">
            {inStock === 'true' || inStock === '1' ? (
              <>
                <button
                  className="group bg-[var(--yellow-primary)] flex items-center rounded-[20px] px-2 py-2 overflow-hidden transition-all duration-300"
                  onClick={(e) => {
                    e.stopPropagation();
                    openModal(product);
                  }}
                >
                  <div className="flex items-center transition-all duration-300 max-w-[22px] group-hover:max-w-[120px]">
                    <img
                      src="/images/shopping-bag-add.svg"
                      alt="Add to shopping cart"
                      className="w-[18px] lg:w-5 shrink-0"
                    />
                    <span className="ml-2 whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                      Add to cart
                    </span>
                  </div>
                </button>

                <div className="flex gap-1">
                  <AddFavourite product={product} />
                  <Button
                    className="rounded-full bg-white p-2 shadow-lg border border-gray-200 transition-all duration-300 ease-in-out focus:outline-none focus:ring-2 focus:ring-black focus:border-black"
                    onClick={(e) => {
                      e.stopPropagation();
                      // Enrich product with category data for proper share URL
                      const enrichedProduct = {
                        ...product,
                        produtCategory: {
                          parent_category: { slug: category },
                          slug: sub_category,
                        },
                      };
                      handleShareProduct(enrichedProduct, user);
                    }}
                  >
                    <span className="sr-only">Share this product</span>
                    <Share2 className="text-black" size="15px" />
                  </Button>
                </div>
              </>
            ) : (
              <p className="text-red-500 font-semibold text-sm">Out of stock</p>
            )}
          </div>
        </div>
      </div>
    </article>
  );
};

export default ProductCard;
